import * as Sentry from "@sentry/react";

/* Error monitoring (Launch readiness — Workstream A).
   Initialized once from main.jsx before the app renders. No-ops unless
   VITE_SENTRY_DSN is set, so local dev and any build without the env var run
   completely clean (nothing is sent). Errors + light performance tracing; no
   session replay — keeps user PII out and stays well within the free tier.
   The DSN is a public, write-only key and safe to ship in the bundle. */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1, // 10% of transactions — light, free-tier friendly
    sendDefaultPii: false, // we attach only a user id (set in AuthProvider)
    beforeSend(event, hint) {
      const ex = hint?.originalException;
      const msg = String((ex && (ex.message ?? ex)) ?? event.message ?? "");
      const url = event.request?.url ?? "";
      // Code-split chunk-load errors: the FIRST one is benign noise
      // (lazyWithReload self-heals by reloading to the fresh build), but a
      // failure right after that reload means the new build itself is broken
      // — previously the blanket ignoreErrors made bad deploys invisible.
      // lazyWithReload stamps sessionStorage before reloading; a chunk error
      // while that stamp is fresh is the post-reload failure: tag and SEND.
      if (
        /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|loading chunk \d+ failed/i.test(
          msg,
        )
      ) {
        let recentReload = false;
        try {
          recentReload =
            Date.now() - Number(sessionStorage.getItem("byb.chunkReloadAt") || 0) < 60000;
        } catch {
          /* sessionStorage unavailable — treat as first failure */
        }
        if (!recentReload) return null; // self-heal in progress — drop
        event.tags = { ...event.tags, chunk_load_post_reload: true };
        return event;
      }
      // Benign Cloudflare Turnstile / Privacy Pass (PrivateToken) 401 handshake
      // on /login — Cloudflare's own script negotiates it; not our bug.
      if (/challenges\.cloudflare\.com|privatetoken|turnstile/i.test(`${msg} ${url}`)) {
        return null;
      }
      return event;
    },
  });
}

export { Sentry };
